import React, { useState } from "react";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { cdnImage } from "../lib/cdn";

const SHORT_CODE_ALPHABET = "abcdefghijklmnopqrstuvwxyz0123456789";
const SHORT_CODE_LENGTH = 6;

const generateShortCode = () => {
    const buf = new Uint8Array(SHORT_CODE_LENGTH);
    if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
        window.crypto.getRandomValues(buf);
    } else {
        for (let i = 0; i < buf.length; i++) buf[i] = Math.floor(Math.random() * 256);
    }
    let out = "";
    for (let i = 0; i < buf.length; i++) {
        out += SHORT_CODE_ALPHABET[buf[i] % SHORT_CODE_ALPHABET.length];
    }
    return out;
};

const mintShortLink = async ({ postId, memberId, campaign }) => {
    for (let attempt = 0; attempt < 3; attempt++) {
        const code = generateShortCode();
        try {
            const payload = {
                postId,
                platform: "other",
                createdAt: serverTimestamp(),
            };
            if (memberId) payload.memberId = memberId;
            if (campaign && typeof campaign === "string" && /^[a-z0-9-]{1,32}$/.test(campaign)) {
                payload.campaign = campaign;
            }
            await setDoc(doc(db, "links", code), payload);
            return code;
        } catch (err) {
            if (attempt === 2) throw err;
        }
    }
    return null;
};

const readMemberId = () => {
    try {
        return window.localStorage.getItem("ambasada.memberId") || null;
    } catch {
        return null;
    }
};

const getImageExtension = (image_link) =>
    (image_link.split('.').pop().split('?')[0] || 'jpg').toLowerCase();

const downloadImage = async (image_link) => {
    try {
        const sourceUrl = cdnImage(image_link, { width: 1600 });
        const response = await fetch(sourceUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.setAttribute('download', `image.${getImageExtension(image_link)}`);
        document.body.appendChild(a);
        a.click();
        a.parentNode.removeChild(a);
        return true;
    } catch (err) {
        console.error('Download failed', err);
        return false;
    }
};

// Sync fallback URL: legacy /s/<docId>?p=other&m=<memberId>. Carries the same
// (docId, platform, memberId, campaign) attribution as the minted /l/<code>
// link — onRequestUrl reads ?p and ?m and decorates the destination with
// utm_medium / utm_term before redirecting.
const buildFallbackUrl = (post_url, memberId) => {
    try {
        const u = new URL(post_url);
        u.searchParams.set('p', 'other');
        if (memberId) u.searchParams.set('m', memberId);
        return u.toString();
    } catch {
        return post_url;
    }
};

const UniversalShareActions = ({ image_link, description, post_url, post_id, track }) => {
    const [copied, setCopied] = useState(false);
    const [downloaded, setDownloaded] = useState(false);

    // Two-tier write so iOS Safari's gesture-bound clipboard plays nice with
    // the async mintShortLink:
    //   1. ClipboardItem with a Promise — the gesture validates at
    //      clipboard.write() but the Blob resolves AFTER mintShortLink has
    //      returned, so we ship the compact /l/<code> URL.
    //   2. Fallback to /s/<id>?p=other&m=<memberId> when ClipboardItem isn't
    //      available. Same five-axis attribution, longer URL.
    const handleCopy = async () => {
        const memberId = readMemberId();
        const fallbackUrl = buildFallbackUrl(post_url, memberId);

        let ok = false;
        if (typeof ClipboardItem !== 'undefined' && navigator.clipboard && navigator.clipboard.write) {
            try {
                const blobPromise = (async () => {
                    let shareUrl = fallbackUrl;
                    if (post_id) {
                        try {
                            const code = await mintShortLink({ postId: post_id, memberId, campaign: track });
                            if (code) shareUrl = `${window.location.origin}/l/${code}`;
                        } catch (err) {
                            console.error('Short link mint failed, using fallback', err);
                        }
                    }
                    return new Blob([`${description} ${shareUrl}`], { type: 'text/plain' });
                })();
                await navigator.clipboard.write([new ClipboardItem({ 'text/plain': blobPromise })]);
                ok = true;
            } catch (err) {
                console.error('ClipboardItem write failed, falling back to writeText', err);
            }
        }

        if (!ok && navigator.clipboard && navigator.clipboard.writeText) {
            try {
                await navigator.clipboard.writeText(`${description} ${fallbackUrl}`);
                ok = true;
            } catch (err) {
                console.error('writeText failed', err);
            }
        }

        if (ok) {
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        }
    };

    const handleDownload = async () => {
        const ok = await downloadImage(image_link);
        if (ok) {
            setDownloaded(true);
            setTimeout(() => setDownloaded(false), 1800);
        }
    };

    return (
        <div className="mt-3 flex items-center justify-between gap-3 text-xs text-brand-muted">
            <button
                type="button"
                onClick={handleDownload}
                className="inline-flex items-center gap-1.5 hover:text-brand-ink transition-colors"
                title="Download the image to your device"
            >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                {downloaded ? "Saved" : "Download"}
            </button>
            <button
                type="button"
                onClick={handleCopy}
                className="inline-flex items-center gap-1.5 hover:text-brand-ink transition-colors"
                title="Copy the caption + link"
            >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
                {copied ? "Copied" : "Text"}
            </button>
        </div>
    );
};

export default UniversalShareActions;
